import React, { useState } from 'react';
import Cookies from 'js-cookie';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useNavigate, Link } from 'react-router-dom';
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Container,
    Typography,
    Chip,
    Paper,
    Divider,
    List,
    ListItem,
    ListItemText,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Autocomplete,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    FormLabel
} from '@mui/material';
import BugReportIcon from '@mui/icons-material/BugReport';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import BuildIcon from '@mui/icons-material/Build';
import ItemDetailsModal from '../../../components/ItemDetailsModal';
import MobileRestriction from '../../../components/MobileRestriction';
import { BACKEND_API_URL } from "../../../utils/api";

const REACT_APP_BACKEND_API_URL = BACKEND_API_URL;

const DebugPage = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [analyzed, setAnalyzed] = useState(false);
    const [circularDeps, setCircularDeps] = useState([]);
    const [stats, setStats] = useState(null);
    
    // Fix dialog state
    const [fixDialogOpen, setFixDialogOpen] = useState(false);
    const [selectedDep, setSelectedDep] = useState(null);
    const [fixOption, setFixOption] = useState('root'); // 'root' or 'select_parent'
    const [newParentId, setNewParentId] = useState('');
    const [foldersList, setFoldersList] = useState([]);
    const [loadingFolders, setLoadingFolders] = useState(false);
    const [fixing, setFixing] = useState(false);
    
    // Item details modal state
    const [itemModalOpen, setItemModalOpen] = useState(false);
    const [selectedItemDetails, setSelectedItemDetails] = useState(null);
    const [loadingItemDetails, setLoadingItemDetails] = useState(false);

    const analyzeCircularDependencies = async () => {
        setLoading(true);
        try {
            const accessToken = Cookies.get('access_token');
            const response = await axios.get(
                `${REACT_APP_BACKEND_API_URL}/api/debug/circular_dependencies/`,
                {
                    headers: {
                        Authorization: `Token ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            setCircularDeps(response.data.circular_dependencies || []);
            setStats({
                totalCount: response.data.total_count,
                totalItemsChecked: response.data.total_items_checked,
            });
            setAnalyzed(true);

            if (response.data.total_count === 0) {
                toast.success('No circular dependencies found! ✓');
            } else {
                toast.error(`Found ${response.data.total_count} circular dependencies!`);
            }
        } catch (error) {
            toast.error('Error analyzing circular dependencies');
            console.error('Error:', error);
        } finally {
            setLoading(false);
        }
    };

    const getItemTypeColor = (type) => {
        switch (type) {
            case 'folder':
                return 'primary';
            case 'link':
                return 'secondary';
            case 'shortcut':
                return 'warning';
            default:
                return 'default';
        }
    };

    const handleItemClick = async (itemId, itemType) => {
        setLoadingItemDetails(true);
        setItemModalOpen(true);
        
        try {
            const accessToken = Cookies.get('access_token');
            const endpoint = itemType === 'folder' 
                ? `/api/folder/${itemId}/`
                : `/api/link/${itemId}/`;
            
            const response = await axios.get(
                `${REACT_APP_BACKEND_API_URL}${endpoint}`,
                {
                    headers: {
                        Authorization: `Token ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                }
            );
            
            setSelectedItemDetails(response.data);
        } catch (error) {
            toast.error('Error loading item details');
            console.error('Error:', error);
            setItemModalOpen(false);
        } finally {
            setLoadingItemDetails(false);
        }
    };

    const handleCloseItemModal = () => {
        setItemModalOpen(false);
        setSelectedItemDetails(null);
    };

    const handleItemUpdate = (itemId, newName) => {
        // Update local state if needed
        if (selectedItemDetails) {
            const itemKey = selectedItemDetails.folder ? 'folder' : 'link';
            setSelectedItemDetails({
                ...selectedItemDetails,
                [itemKey]: {
                    ...selectedItemDetails[itemKey],
                    name: newName
                }
            });
        }
    };

    const loadFoldersList = async () => {
        setLoadingFolders(true);
        try {
            const accessToken = Cookies.get('access_token');
            const response = await axios.get(
                `${REACT_APP_BACKEND_API_URL}/api/folders_list/`,
                {
                    headers: {
                        Authorization: `Token ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                }
            );
            setFoldersList(response.data || []);
        } catch (error) {
            toast.error('Error loading folders list');
            console.error('Error:', error);
        } finally {
            setLoadingFolders(false);
        }
    };

    const handleOpenFixDialog = (dep) => {
        setSelectedDep(dep);
        setFixOption('root');
        setNewParentId('');
        setFixDialogOpen(true);
        loadFoldersList();
    };

    const handleCloseFixDialog = () => {
        setFixDialogOpen(false);
        setSelectedDep(null);
        setNewParentId('');
    };

    const handleFixCircularDependency = async () => {
        if (!selectedDep) return;

        setFixing(true);
        try {
            const accessToken = Cookies.get('access_token');
            const payload = {
                new_parent_id: fixOption === 'root' ? null : parseInt(newParentId)
            };

            const response = await axios.post(
                `${REACT_APP_BACKEND_API_URL}/api/debug/fix_circular_dependency/${selectedDep.item_id}/`,
                payload,
                {
                    headers: {
                        Authorization: `Token ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (response.data.success) {
                toast.success(response.data.message);
                handleCloseFixDialog();
                // Re-analyze after fixing
                setTimeout(() => analyzeCircularDependencies(), 500);
            }
        } catch (error) {
            const errorMsg = error.response?.data?.error || 'Error fixing circular dependency';
            toast.error(errorMsg);
            console.error('Error:', error);
        } finally {
            setFixing(false);
        }
    };

    // Mobile portrait mode check
    const mobilePortrait = window?.innerWidth <= 600;
    if (mobilePortrait) {
        return <MobileRestriction />;
    }

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Box sx={{ mb: 4, textAlign: 'center' }}>
                <BugReportIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
                <Typography variant="h3" gutterBottom>
                    Debug Tools
                </Typography>
                <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                    Analyze and identify circular dependencies in your item hierarchy
                </Typography>
            </Box>

            <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="h5">
                        Circular Dependency Analysis
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                            variant="outlined"
                            color="success"
                            size="large"
                            startIcon={<BuildIcon />}
                            onClick={() => navigate('/move-to-root')}
                        >
                            Move to Root
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                            onClick={analyzeCircularDependencies}
                            disabled={loading}
                        >
                            {loading ? 'Analyzing...' : analyzed ? 'Re-analyze' : 'Analyze'}
                        </Button>
                    </Box>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Click "Analyze" to scan all items for circular parent-child relationships. 
                    Use "Move to Root" to manually move any item to the root level by its ID.
                </Typography>

                {stats && (
                    <Box sx={{ mt: 3 }}>
                        <Alert
                            severity={stats.totalCount === 0 ? 'success' : 'error'}
                            icon={stats.totalCount === 0 ? <CheckCircleIcon /> : <ErrorIcon />}
                        >
                            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                {stats.totalCount === 0
                                    ? '✓ All Clear! No circular dependencies found.'
                                    : `⚠ Found ${stats.totalCount} circular ${stats.totalCount === 1 ? 'dependency' : 'dependencies'}`}
                            </Typography>
                            <Typography variant="body2">
                                Checked {stats.totalItemsChecked} items in total
                            </Typography>
                        </Alert>
                    </Box>
                )}
            </Paper>

            {analyzed && circularDeps.length > 0 && (
                <Paper elevation={3} sx={{ p: 3 }}>
                    <Typography variant="h5" gutterBottom sx={{ color: 'error.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ErrorIcon /> Circular Dependencies Found
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        The following items have circular parent references that need to be fixed:
                    </Typography>

                    <List>
                        {circularDeps.map((dep, index) => (
                            <React.Fragment key={index}>
                                {index > 0 && <Divider sx={{ my: 2 }} />}
                                <Card variant="outlined" sx={{ mb: 2, bgcolor: 'error.light', borderColor: 'error.main' }}>
                                    <CardContent>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                            <ErrorIcon color="error" />
                                            <Typography variant="h6">
                                                Circular Reference #{index + 1}
                                            </Typography>
                                            <Chip
                                                label={dep.item_type}
                                                size="small"
                                                color={getItemTypeColor(dep.item_type)}
                                            />
                                        </Box>

                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                                Problem Item:
                                            </Typography>
                                            <Typography variant="body2" sx={{ ml: 2 }}>
                                                {dep.item_name} <span style={{ color: '#666' }}>(ID: {dep.item_id})</span>
                                            </Typography>
                                        </Box>

                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                                Loops back to:
                                            </Typography>
                                            <Typography variant="body2" sx={{ ml: 2 }}>
                                                {dep.circular_at_name} <span style={{ color: '#666' }}>(ID: {dep.circular_at_id})</span>
                                            </Typography>
                                        </Box>

                                        {dep.path && dep.path.length > 0 && (
                                            <Box>
                                                <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                                    Parent Chain (length: {dep.path_length}):
                                                </Typography>
                                                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                                                        {dep.path.map((item, pathIndex) => (
                                                            <React.Fragment key={pathIndex}>
                                                                <Chip
                                                                    label={`${item.name} (${item.id})`}
                                                                    size="small"
                                                                    color={getItemTypeColor(item.type)}
                                                                    variant={pathIndex === 0 ? 'filled' : 'outlined'}
                                                                    onClick={() => handleItemClick(item.id, item.type)}
                                                                    sx={{ 
                                                                        cursor: 'pointer',
                                                                        '&:hover': {
                                                                            opacity: 0.8,
                                                                            transform: 'scale(1.05)',
                                                                            transition: 'all 0.2s',
                                                                            boxShadow: 2
                                                                        }
                                                                    }}
                                                                />
                                                                {pathIndex < dep.path.length - 1 && (
                                                                    <ArrowRightAltIcon fontSize="small" />
                                                                )}
                                                            </React.Fragment>
                                                        ))}
                                                        <ArrowRightAltIcon fontSize="small" sx={{ color: 'error.main' }} />
                                                        <Chip
                                                            label="🔄 LOOPS BACK"
                                                            size="small"
                                                            color="error"
                                                            sx={{ fontWeight: 'bold' }}
                                                        />
                                                    </Box>
                                                </Paper>
                                            </Box>
                                        )}

                                        <Box sx={{ mt: 2, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                <Box>
                                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                        💡 How to fix:
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        Update the parent of item <strong>{dep.item_name} ({dep.item_id})</strong> to
                                                        break the circular reference, or set it to NULL to make it a root item.
                                                    </Typography>
                                                </Box>
                                                <Button
                                                    variant="contained"
                                                    color="success"
                                                    size="small"
                                                    startIcon={<BuildIcon />}
                                                    onClick={() => handleOpenFixDialog(dep)}
                                                >
                                                    Fix Now
                                                </Button>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </React.Fragment>
                        ))}
                    </List>
                </Paper>
            )}

            {analyzed && circularDeps.length === 0 && (
                <Paper elevation={3} sx={{ p: 4, textAlign: 'center', bgcolor: 'success.light' }}>
                    <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                    <Typography variant="h5" gutterBottom>
                        All Clear!
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        No circular dependencies detected in your item hierarchy.
                        Your data structure is healthy! ✓
                    </Typography>
                </Paper>
            )}

            {/* Fix Dialog */}
            <Dialog open={fixDialogOpen} onClose={handleCloseFixDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BuildIcon color="primary" />
                        Fix Circular Dependency
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {selectedDep && (
                        <Box>
                            <Alert severity="info" sx={{ mb: 3 }}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                    Problem Item:
                                </Typography>
                                <Typography variant="body2">
                                    {selectedDep.item_name} ({selectedDep.item_type}) - ID: {selectedDep.item_id}
                                </Typography>
                            </Alert>

                            <FormControl component="fieldset" fullWidth>
                                <FormLabel component="legend" sx={{ mb: 2 }}>
                                    Choose how to fix this circular dependency:
                                </FormLabel>
                                <RadioGroup
                                    value={fixOption}
                                    onChange={(e) => setFixOption(e.target.value)}
                                >
                                    <FormControlLabel
                                        value="root"
                                        control={<Radio />}
                                        label={
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                    Make it a Root Item (Recommended)
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Remove parent relationship - item will have no parent
                                                </Typography>
                                            </Box>
                                        }
                                    />
                                    <FormControlLabel
                                        value="select_parent"
                                        control={<Radio />}
                                        label={
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                    Set a Different Parent
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Choose a new parent folder from the list
                                                </Typography>
                                            </Box>
                                        }
                                    />
                                </RadioGroup>
                            </FormControl>

                            {fixOption === 'select_parent' && (
                                <Box sx={{ mt: 3 }}>
                                    <Autocomplete
                                        options={foldersList.filter(f => 
                                            f.id !== selectedDep.item_id && 
                                            !selectedDep.path.some(p => p.id === f.id)
                                        )}
                                        getOptionLabel={(option) => `${option.name} (ID: ${option.id})`}
                                        loading={loadingFolders}
                                        value={foldersList.find(f => f.id === parseInt(newParentId)) || null}
                                        onChange={(event, newValue) => {
                                            setNewParentId(newValue ? newValue.id.toString() : '');
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Select New Parent"
                                                placeholder="Search folders..."
                                                helperText="Cannot select the item itself or items in its circular path"
                                            />
                                        )}
                                    />
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseFixDialog} disabled={fixing}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleFixCircularDependency}
                        variant="contained"
                        color="primary"
                        disabled={fixing || (fixOption === 'select_parent' && !newParentId)}
                        startIcon={fixing ? <CircularProgress size={16} /> : <BuildIcon />}
                    >
                        {fixing ? 'Fixing...' : 'Fix Circular Dependency'}
                    </Button>
                </DialogActions>
            </Dialog>

        </Container>
    );
};

export default DebugPage;

